import { z } from 'zod';
/**
 * Visual review (package D), media half: artifacts pinned to the commit they were captured at,
 * with a sidecar of stats, and before/after pairing with stale-baseline detection. Additive optional fields on artifact events/index entries plus pure helpers shared by the
 * daemon and the web app. (No imports from artifact-ref: that module spreads these fields.)
 */
// ---- media provenance ---------------------------------------------------------------------------
const Sha = z.string().regex(/^[0-9a-f]{7,64}$/);
/** Where and when a media artifact was captured: the proof that a "before" really is the base. */
export const ArtifactProvenance = z.strictObject({
    /** Exact commit the worktree was at (HEAD). */
    commit: Sha,
    /** Tree hash of that commit (stable across rebases that keep content). */
    tree: Sha.optional(),
    branch: z.string().max(256).optional(),
    /** The project's base commit (merge-base with the target branch) when captured. */
    baseCommit: Sha.optional(),
    /** The worktree had uncommitted changes: the capture may not match `commit`. */
    dirty: z.boolean().optional(),
    runId: z.string().min(1).max(256).optional(),
    taskId: z.string().min(1).max(256).optional(),
    /** `before`: captured at the base commit; `after`: captured on the head. */
    role: z.enum(['before', 'after']).optional(),
    /** Pairing key: the capture name (path relative to the worktree), same at base and head. */
    key: z.string().min(1).max(512),
    /** The `artifacts.globs` entry that matched. */
    glob: z.string().max(512).optional(),
    capturedAt: z.number().int().nonnegative(),
});
/** Sidecar JSON stats (e.g. `level-videos/<id>.mp4.json`): flat, bounded, shown beside the media. */
export const SIDECAR_MAX_KEYS = 40;
export const ArtifactSidecar = z.strictObject({
    /** Sidecar file, relative to the worktree. */
    path: z.string().min(1).max(512),
    stats: z
        .record(z.string().min(1).max(80), z.union([z.string().max(500), z.number(), z.boolean(), z.null()]))
        .refine((r) => Object.keys(r).length <= SIDECAR_MAX_KEYS, 'too many stats'),
});
/** Additive artifact event / index entry fields (spread into `AutoArtifactFields`). */
export const VisualReviewArtifactFields = {
    provenance: ArtifactProvenance.optional(),
    sidecar: ArtifactSidecar.optional(),
};
const IMAGE = /\.(png|jpe?g|gif|webp|avif)$/i;
const VIDEO = /\.(mp4|mov|webm|m4v)$/i;
export function mediaKindOf(name) {
    return IMAGE.test(name) ? 'image' : VIDEO.test(name) ? 'video' : undefined;
}
/** Flatten arbitrary sidecar JSON into bounded primitive stats (nested keys joined with `.`). */
export function flattenSidecar(raw) {
    const out = {};
    const walk = (v, key, depth) => {
        if (Object.keys(out).length >= SIDECAR_MAX_KEYS || !key)
            return;
        if (v === null || typeof v === 'boolean')
            out[key.slice(0, 80)] = v;
        else if (typeof v === 'number') {
            if (Number.isFinite(v))
                out[key.slice(0, 80)] = v;
        }
        else if (typeof v === 'string')
            out[key.slice(0, 80)] = v.slice(0, 500);
        else if (Array.isArray(v)) {
            if (v.length && v.every((x) => typeof x !== 'object' || x === null))
                out[key.slice(0, 80)] = v.slice(0, 20).join(', ').slice(0, 500);
        }
        else if (typeof v === 'object' && depth < 3)
            for (const [k, x] of Object.entries(v))
                walk(x, depth === 0 && !key ? k : `${key}.${k}`, depth + 1);
    };
    if (raw && typeof raw === 'object' && !Array.isArray(raw))
        for (const [k, x] of Object.entries(raw))
            walk(x, k, 1);
    return out;
}
const short = (s) => (s ? s.slice(0, 9) : 'unknown');
/**
 * Is `before` really what the base looked like? `base` is the commit the "after" was built on
 * (`after.provenance.baseCommit`) or, when known, the project's current base.
 */
export function staleBaseline(before, base) {
    const p = before.provenance;
    if (!p)
        return {
            reason: 'no-provenance',
            message: 'This "before" has no recorded commit, so it may not show the current base.',
        };
    if (p.dirty)
        return {
            reason: 'dirty',
            message: `This "before" was captured with uncommitted changes on top of ${short(p.commit)}.`,
        };
    const same = (a, b) => a.startsWith(b) || b.startsWith(a);
    if (base.commit && !same(p.commit, base.commit))
        return {
            reason: 'different-commit',
            message: `This "before" is from ${short(p.commit)}, not the base ${short(base.commit)} the change was built on.`,
        };
    if (base.current && !same(p.commit, base.current))
        return {
            reason: 'base-moved',
            message: `The base moved to ${short(base.current)} since this "before" (${short(p.commit)}) was captured.`,
        };
    return undefined;
}
/** Roles from provenance (captured at the base commit = before). */
export function roleOf(a) {
    const p = a.provenance;
    if (!p)
        return undefined;
    if (p.role)
        return p.role;
    if (p.baseCommit && (p.commit.startsWith(p.baseCommit) || p.baseCommit.startsWith(p.commit)))
        return 'before';
    return 'after';
}
/**
 * Pair each head capture ("after") with the capture of the same name at the base ("before").
 * The best baseline is the newest "before" at exactly the base commit the "after" was built on;
 * failing that, the newest "before" of that name is paired but flagged stale. Captures without
 * provenance never pair. Pairs are ordered by the "after" capture time, newest first.
 */
export function pairBeforeAfter(items, opts = {}) {
    const befores = new Map();
    const afters = [];
    const unpaired = [];
    for (const it of items) {
        const role = roleOf(it);
        if (!role || !it.provenance)
            unpaired.push(it);
        else if (role === 'before') {
            const k = it.provenance.key;
            befores.set(k, [...(befores.get(k) ?? []), it]);
        }
        else
            afters.push(it);
    }
    const used = new Set();
    const pairs = [];
    for (const after of [...afters].sort((a, b) => b.createdAt - a.createdAt)) {
        const p = after.provenance;
        const cands = (befores.get(p.key) ?? []).sort((a, b) => b.createdAt - a.createdAt);
        const exact = p.baseCommit
            ? cands.find((c) => {
                const bc = c.provenance?.commit ?? '';
                return !!p.baseCommit && (bc.startsWith(p.baseCommit) || p.baseCommit.startsWith(bc));
            })
            : undefined;
        const before = exact ?? cands[0];
        if (!before) {
            pairs.push({ key: p.key, after });
            continue;
        }
        used.add(before.id);
        const stale = staleBaseline(before, {
            commit: p.baseCommit,
            current: opts.currentBase,
        });
        pairs.push({ key: p.key, after, before, ...(stale ? { stale } : {}) });
    }
    for (const list of befores.values())
        for (const b of list)
            if (!used.has(b.id))
                unpaired.push(b);
    return { pairs, unpaired };
}
//# sourceMappingURL=media-provenance.js.map